/**
 * emotionCategoryRegistrySlice.js
 *
 * Client state for the admin-editable emotion→category translation matrix.
 * Mirrors pathRegistrySlice's transport (raw axios + bearer from localStorage,
 * URLs from `config`). Admin-gated on the server (adminAuth); the page is also
 * behind an admin route.
 *
 * Thunks:
 *   - fetchMatrix()                          GET    /emotion-category/matrix
 *   - createMapping({ emotion, category })   POST   /emotion-category/matrix
 *   - updateMapping({ emotion, category })   PUT    /emotion-category/matrix/:emotion
 *   - deleteMapping(emotion)                 DELETE /emotion-category/matrix/:emotion
 *   - resetMapping(emotion)                  POST   /emotion-category/matrix/:emotion/reset
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { config } from '../constants/config';

const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const itemUrl = (emotion) =>
  `${config.API_EMOTION_CATEGORY_MATRIX}/${encodeURIComponent(emotion)}`;

const unwrap = (resp) => resp?.data?.data ?? resp?.data;
const errMsg = (e) =>
  e?.response?.data?.error?.message || e?.response?.data?.message || e.message;

export const fetchMatrix = createAsyncThunk(
  'emotionCategoryRegistry/fetchMatrix',
  async (_, { rejectWithValue }) => {
    try {
      const resp = await axios.get(config.API_EMOTION_CATEGORY_MATRIX, authHeaders());
      return unwrap(resp);
    } catch (e) {
      return rejectWithValue({ message: errMsg(e) });
    }
  }
);

export const createMapping = createAsyncThunk(
  'emotionCategoryRegistry/createMapping',
  async ({ emotion, category }, { rejectWithValue }) => {
    try {
      const resp = await axios.post(
        config.API_EMOTION_CATEGORY_MATRIX,
        { emotion, category },
        authHeaders()
      );
      return unwrap(resp)?.mapping;
    } catch (e) {
      return rejectWithValue({ message: errMsg(e) });
    }
  }
);

export const updateMapping = createAsyncThunk(
  'emotionCategoryRegistry/updateMapping',
  async ({ emotion, category }, { rejectWithValue }) => {
    try {
      const resp = await axios.put(itemUrl(emotion), { category }, authHeaders());
      return unwrap(resp)?.mapping;
    } catch (e) {
      return rejectWithValue({ message: errMsg(e) });
    }
  }
);

export const deleteMapping = createAsyncThunk(
  'emotionCategoryRegistry/deleteMapping',
  async (emotion, { rejectWithValue }) => {
    try {
      await axios.delete(itemUrl(emotion), authHeaders());
      return { emotion: String(emotion).trim().toLowerCase() };
    } catch (e) {
      return rejectWithValue({ message: errMsg(e) });
    }
  }
);

export const resetMapping = createAsyncThunk(
  'emotionCategoryRegistry/resetMapping',
  async (emotion, { rejectWithValue }) => {
    try {
      const resp = await axios.post(`${itemUrl(emotion)}/reset`, {}, authHeaders());
      return unwrap(resp)?.mapping;
    } catch (e) {
      return rejectWithValue({ message: errMsg(e) });
    }
  }
);

const categoryUrl = (category) =>
  `${config.API_EMOTION_CATEGORY_CATEGORIES}/${encodeURIComponent(category)}`;

export const updateCategoryPrompt = createAsyncThunk(
  'emotionCategoryRegistry/updateCategoryPrompt',
  async ({ category, prompt, directive }, { rejectWithValue }) => {
    try {
      const resp = await axios.put(categoryUrl(category), { prompt, directive }, authHeaders());
      return unwrap(resp)?.category;
    } catch (e) {
      return rejectWithValue({ message: errMsg(e) });
    }
  }
);

export const resetCategoryPrompt = createAsyncThunk(
  'emotionCategoryRegistry/resetCategoryPrompt',
  async (category, { rejectWithValue }) => {
    try {
      const resp = await axios.post(`${categoryUrl(category)}/reset`, {}, authHeaders());
      return unwrap(resp)?.category;
    } catch (e) {
      return rejectWithValue({ message: errMsg(e) });
    }
  }
);

export const repropagateCategory = createAsyncThunk(
  'emotionCategoryRegistry/repropagateCategory',
  async (category, { rejectWithValue }) => {
    try {
      const resp = await axios.post(`${categoryUrl(category)}/repropagate`, {}, authHeaders());
      return unwrap(resp);
    } catch (e) {
      return rejectWithValue({ message: errMsg(e) });
    }
  }
);

// --- Per-emotion sample passages + generation ---

export const generateSampleText = createAsyncThunk(
  'emotionCategoryRegistry/generateSampleText',
  async ({ emotion, nudge }, { rejectWithValue }) => {
    try {
      const resp = await axios.post(`${itemUrl(emotion)}/generate`, { nudge }, authHeaders());
      return unwrap(resp)?.mapping;
    } catch (e) {
      return rejectWithValue({ message: errMsg(e) });
    }
  }
);

export const updateSampleText = createAsyncThunk(
  'emotionCategoryRegistry/updateSampleText',
  async ({ emotion, sampleText, genNudge }, { rejectWithValue }) => {
    try {
      const resp = await axios.put(`${itemUrl(emotion)}/sample`, { sampleText, genNudge }, authHeaders());
      return unwrap(resp)?.mapping;
    } catch (e) {
      return rejectWithValue({ message: errMsg(e) });
    }
  }
);

export const repropagateEmotion = createAsyncThunk(
  'emotionCategoryRegistry/repropagateEmotion',
  async (emotion, { rejectWithValue }) => {
    try {
      const resp = await axios.post(`${itemUrl(emotion)}/repropagate`, {}, authHeaders());
      return unwrap(resp);
    } catch (e) {
      return rejectWithValue({ message: errMsg(e) });
    }
  }
);

export const fetchGenTemplate = createAsyncThunk(
  'emotionCategoryRegistry/fetchGenTemplate',
  async (_, { rejectWithValue }) => {
    try {
      const resp = await axios.get(config.API_EMOTION_CATEGORY_GEN_TEMPLATE, authHeaders());
      return unwrap(resp);
    } catch (e) {
      return rejectWithValue({ message: errMsg(e) });
    }
  }
);

export const updateGenTemplate = createAsyncThunk(
  'emotionCategoryRegistry/updateGenTemplate',
  async ({ system, template }, { rejectWithValue }) => {
    try {
      const resp = await axios.put(config.API_EMOTION_CATEGORY_GEN_TEMPLATE, { system, template }, authHeaders());
      return unwrap(resp);
    } catch (e) {
      return rejectWithValue({ message: errMsg(e) });
    }
  }
);

export const resetGenTemplate = createAsyncThunk(
  'emotionCategoryRegistry/resetGenTemplate',
  async (_, { rejectWithValue }) => {
    try {
      const resp = await axios.post(`${config.API_EMOTION_CATEGORY_GEN_TEMPLATE}/reset`, {}, authHeaders());
      return unwrap(resp);
    } catch (e) {
      return rejectWithValue({ message: errMsg(e) });
    }
  }
);

const upsertCategory = (list, row) => {
  if (!row) return list;
  const i = list.findIndex((c) => c.category === row.category);
  if (i === -1) return list;
  const next = list.slice();
  next[i] = { ...next[i], ...row };
  return next;
};

const upsert = (mappings, row) => {
  if (!row) return mappings;
  const i = mappings.findIndex((m) => m.emotion === row.emotion);
  if (i === -1) return [...mappings, row].sort((a, b) => a.emotion.localeCompare(b.emotion));
  const next = mappings.slice();
  next[i] = row;
  return next;
};

const mergeMappingSample = (mappings, row) => {
  if (!row) return mappings;
  return mappings.map((m) => (m.emotion === row.emotion ? { ...m, ...row } : m));
};

const initialState = {
  categories: [],
  categoryPrompts: [],
  mappings: [],
  genTemplate: null,
  meta: null,
  loading: false,
  generating: {}, // emotion -> bool (resample in flight)
  error: null,
  success: null,
};

const slice = createSlice({
  name: 'emotionCategoryRegistry',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearSuccess(state) {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMatrix.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMatrix.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload?.categories || [];
        state.categoryPrompts = action.payload?.categoryPrompts || [];
        state.mappings = action.payload?.mappings || [];
        state.genTemplate = action.payload?.genTemplate || state.genTemplate;
        state.meta = action.payload?.meta || null;
      })
      .addCase(fetchMatrix.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to load matrix';
      })

      .addCase(createMapping.fulfilled, (state, action) => {
        state.mappings = upsert(state.mappings, action.payload);
        state.success = `Added "${action.payload?.emotion}" → ${action.payload?.category}`;
      })
      .addCase(createMapping.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to add mapping';
      })

      .addCase(updateMapping.fulfilled, (state, action) => {
        state.mappings = upsert(state.mappings, action.payload);
        state.success = `Updated "${action.payload?.emotion}" → ${action.payload?.category}`;
      })
      .addCase(updateMapping.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to update mapping';
      })

      .addCase(deleteMapping.fulfilled, (state, action) => {
        const emotion = action.payload?.emotion;
        state.mappings = state.mappings.filter((m) => m.emotion !== emotion);
        state.success = `Removed "${emotion}"`;
      })
      .addCase(deleteMapping.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to remove mapping';
      })

      .addCase(resetMapping.fulfilled, (state, action) => {
        state.mappings = upsert(state.mappings, action.payload);
        state.success = `Reset "${action.payload?.emotion}" to default (${action.payload?.category})`;
      })
      .addCase(resetMapping.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to reset mapping';
      })

      .addCase(updateCategoryPrompt.fulfilled, (state, action) => {
        state.categoryPrompts = upsertCategory(state.categoryPrompts, action.payload);
        state.success = `Saved "${action.payload?.category}" prompt`;
      })
      .addCase(updateCategoryPrompt.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to save prompt';
      })

      .addCase(resetCategoryPrompt.fulfilled, (state, action) => {
        state.categoryPrompts = upsertCategory(state.categoryPrompts, action.payload);
        state.success = `Reset "${action.payload?.category}" prompt to default`;
      })
      .addCase(resetCategoryPrompt.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to reset prompt';
      })

      .addCase(generateSampleText.pending, (state, action) => {
        state.generating[action.meta.arg?.emotion] = true;
      })
      .addCase(generateSampleText.fulfilled, (state, action) => {
        state.generating[action.payload?.emotion] = false;
        state.mappings = mergeMappingSample(state.mappings, action.payload);
        state.success = `Generated passage for "${action.payload?.emotion}"`;
      })
      .addCase(generateSampleText.rejected, (state, action) => {
        state.generating[action.meta.arg?.emotion] = false;
        state.error = action.payload?.message || 'Generation failed';
      })

      .addCase(updateSampleText.fulfilled, (state, action) => {
        state.mappings = mergeMappingSample(state.mappings, action.payload);
        state.success = `Saved passage for "${action.payload?.emotion}"`;
      })
      .addCase(updateSampleText.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to save passage';
      })

      .addCase(repropagateEmotion.fulfilled, (state, action) => {
        const { emotion, updated, skippedRecorded } = action.payload || {};
        state.success =
          `Re-propagated "${emotion}": updated ${updated} un-recorded sample(s)` +
          (skippedRecorded ? `, left ${skippedRecorded} recorded one(s) untouched` : '');
      })
      .addCase(repropagateEmotion.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to re-propagate';
      })

      .addCase(fetchGenTemplate.fulfilled, (state, action) => {
        state.genTemplate = action.payload || state.genTemplate;
      })
      .addCase(updateGenTemplate.fulfilled, (state, action) => {
        state.genTemplate = action.payload || state.genTemplate;
        state.success = 'Generation prompt saved';
      })
      .addCase(updateGenTemplate.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to save generation prompt';
      })
      .addCase(resetGenTemplate.fulfilled, (state, action) => {
        state.genTemplate = action.payload || state.genTemplate;
        state.success = 'Generation prompt reset to default';
      })

      .addCase(repropagateCategory.fulfilled, (state, action) => {
        const { category, updated, skippedRecorded } = action.payload || {};
        state.success =
          `Re-propagated "${category}": updated ${updated} un-recorded sample(s)` +
          (skippedRecorded ? `, left ${skippedRecorded} recorded one(s) untouched` : '');
      })
      .addCase(repropagateCategory.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to re-propagate';
      });
  },
});

export const { clearError, clearSuccess } = slice.actions;
export default slice.reducer;
